import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'
import React, { useEffect, useMemo, useState } from 'react'

import { ALBUM } from '@disco/constants/ItemTypes'
import { useTranslation } from '@disco/i18n/use-translation'
import {
  clientLibraryAlbumsLoad,
  clientLibraryAlbumsLoadNext,
  clientLibraryAlbumsUpdate,
  clientLibraryFeaturedAlbumsSet,
  clientLibraryFeaturedAlbumsUpdate,
} from '@disco/state-management/actions/client-library-album-actions'
import { getClientLibraryAlbums } from '@disco/state-management/selectors/client-libraries'

import type { ClientLibraryId } from 'types/client-library'
import type { Playlist, PlaylistId } from 'types/playlist'
import type { State } from 'types/redux'
import type { PopoverItemType } from 'types/ui'

import emptyArtists1x from 'assets/images/empty-artists.png'
import emptyArtists2x from 'assets/images/empty-artists@2x.png'
import emptySearch1x from 'assets/images/empty-search.png'
import emptySearch2x from 'assets/images/empty-search@2x.png'

import AlbumGridItem from 'components/common/album-grid-item'
import Row from 'components/common/row'
import { Tab, Tabs } from 'components/ui'
import AspectRatio from 'components/ui/aspect-ratio'
import Draggable from 'components/ui/draggable'
import EllipsisMenu from 'components/ui/ellipsis-menu'
import EmptyState from 'components/ui/empty-state'
import Grid from 'components/ui/grid'
import GridItem from 'components/ui/grid-item'
import InfiniteScroll from 'components/ui/infinite-scroll'
import Input from 'components/ui/input'
import Sortable from 'components/ui/sortable'

import { useDispatch, useSelector } from 'hooks/use-redux'

import { type AlbumVariant, getAlbumLabels } from './album-labels'

type Props = {
  libraryId: ClientLibraryId
  basePath: string
  variant: AlbumVariant
  contentClassName?: string
}

/**
 * Published and featured albums of a catalog: publishing, featuring, reordering
 * featured albums and linking to their add and edit modals.
 * @param libraryId - ID of the catalog
 * @param basePath - Path the add (`/add`) and edit (`/:playlistId`) modal routes are nested under
 * @param variant - `albums` for catalogs, `releases` for artist profiles
 * @param contentClassName - Class for the element wrapping each tab's albums, e.g. to make it scroll
 */
const LibraryAlbumsManager = ({
  libraryId,
  basePath,
  variant,
  contentClassName,
}: Props): React.ReactNode => {
  const { t } = useTranslation('catalogs-management')
  const labels = getAlbumLabels(t, variant)
  const dispatch = useDispatch()
  const albums = useSelector((state: State) =>
    getClientLibraryAlbums(state, libraryId),
  )
  const [selectedTab, setSelectedTab] = useState<number>(0)
  const [query, setQuery] = useState<string>('')
  const [isLoadingNext, setIsLoadingNext] = useState<boolean>(false)
  const debouncedQuery = useMemo(
    () =>
      debounce(query => {
        dispatch(clientLibraryAlbumsLoad(libraryId, query))
      }, 300),
    [dispatch, libraryId],
  )
  useEffect(() => () => debouncedQuery.cancel(), [debouncedQuery])
  useEffect(() => {
    // A search still waiting to fire would overwrite the list loaded here
    debouncedQuery.cancel()

    if (selectedTab === 0) {
      dispatch(clientLibraryAlbumsLoad(libraryId, query))
    }

    if (selectedTab === 1) {
      setQuery('')
      dispatch(clientLibraryAlbumsLoad(libraryId, '', true))
    }

    setIsLoadingNext(false)
  }, [selectedTab, libraryId, dispatch, debouncedQuery])

  const updateSearchQuery = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoadingNext(false)
    setQuery(event.target.value)
    debouncedQuery(event.target.value)
  }

  const toggleIsFeatured = (album: Playlist) => () => {
    let add = []
    let remove = []

    if (album['is_featured']) {
      remove.push(album['playlist_id'])
    } else {
      add.push(album['playlist_id'])
    }

    dispatch(clientLibraryFeaturedAlbumsUpdate(libraryId, add, remove))
  }

  const removeAlbumFromLibrary = (id: PlaylistId) => () => {
    dispatch(clientLibraryAlbumsUpdate(libraryId, [], [id]))
  }

  const setFeaturedAlbums = item => {
    const order: PlaylistId[] = item?.order.map(Number)

    if (!isEqual(albums?.list, order)) {
      dispatch(clientLibraryFeaturedAlbumsSet(libraryId, order))
    }
  }

  const loadNext = (next: string) => {
    setIsLoadingNext(true)
    dispatch(clientLibraryAlbumsLoadNext(libraryId, next))
  }

  const getEllipsisMenuOptions = (album: Playlist): PopoverItemType[] => [
    {
      label: album['is_featured']
        ? labels.removeFromFeatured
        : labels.addToFeatured,
      onClick: toggleIsFeatured(album),
    },
    {
      label: labels.edit,
      link: {
        url: `${basePath}/${album['playlist_id']}`,
      },
      type: 'router-link',
    },
    {
      label: labels.unpublish,
      onClick: removeAlbumFromLibrary(album['playlist_id']),
    },
  ]

  const renderContent = (albums: {
    loading: boolean
    loaded: boolean
    nextPage: string | null
    items: Record<PlaylistId, Playlist>
    list: PlaylistId[]
  }) => (
    <div className={contentClassName}>
      {!isLoadingNext && albums.loading
        ? t('catalogs-management:loading', 'Loading...')
        : renderAlbums(albums)}
    </div>
  )

  const renderAlbums = (albums: {
    loading: boolean
    loaded: boolean
    nextPage: string | null
    items: Record<PlaylistId, Playlist>
    list: PlaylistId[]
  }) => {
    const items: Playlist[] = albums.list.map(
      (id: PlaylistId) => albums.items[id],
    )
    return albums.loaded && albums.list.length === 0 ? (
      renderEmptyState()
    ) : (
      <InfiniteScroll
        next={albums.nextPage}
        loadNext={loadNext}
        loadingNode={t('catalogs-management:loading', 'Loading...')}
      >
        {selectedTab === 0 && (
          <Grid size="medium">
            {renderAddItem()}
            {items.map(renderItem)}
          </Grid>
        )}
        {selectedTab === 1 && (
          <Sortable
            accept={[ALBUM]}
            id={`${libraryId}-featured-albums`}
            items={items}
            keyExtractor={(item: Playlist) => String(item['playlist_id'])}
            renderItem={renderItem}
            onDrop={setFeaturedAlbums}
          >
            {items => <Grid size="medium">{items}</Grid>}
          </Sortable>
        )}
      </InfiniteScroll>
    )
  }

  const renderAddItem = (): React.ReactNode => (
    <GridItem
      link={{
        href: `${basePath}/add`,
      }}
      media={
        <AspectRatio fill="#5f63ee" ratio={1}>
          <svg width="27" height="26" viewBox="0 0 27 26">
            <path
              fill="#fff"
              d="M24,14.8333333 L14.8333333,14.8333333 L14.8333333,24 C14.8333333,25.0083333 14.0083333,25.8333333 13,25.8333333 C11.9916667,25.8333333 11.1666667,25.0083333 11.1666667,24 L11.1666667,14.8333333 L2,14.8333333 C0.991666667,14.8333333 0.166666667,14.0083333 0.166666667,13 C0.166666667,11.9916667 0.991666667,11.1666667 2,11.1666667 L11.1666667,11.1666667 L11.1666667,2 C11.1666667,0.991666667 11.9916667,0.166666667 13,0.166666667 C14.0083333,0.166666667 14.8333333,0.991666667 14.8333333,2 L14.8333333,11.1666667 L24,11.1666667 C25.0083333,11.1666667 25.8333333,11.9916667 25.8333333,13 C25.8333333,14.0083333 25.0083333,14.8333333 24,14.8333333 Z"
              transform="translate(.5)"
            />
          </svg>
        </AspectRatio>
      }
      title={labels.publishNew}
    />
  )

  const renderItem = (album: Playlist): React.ReactNode => {
    if (!album) return null
    const item: React.ReactNode = (
      <AlbumGridItem
        key={album['playlist_id']}
        album={album}
        status={
          album['is_featured']
            ? t('catalogs-management:featured', 'Featured')
            : undefined
        }
        overlayActions={
          <EllipsisMenu
            compact
            appearance="black"
            options={getEllipsisMenuOptions(album)}
            icon="ellipsis"
            hasArrow={false}
          />
        }
      />
    )
    return selectedTab === 0 ? (
      item
    ) : (
      <Draggable
        key={album['playlist_id']}
        id={album['playlist_id']}
        type={ALBUM}
      >
        {item}
      </Draggable>
    )
  }

  const renderEmptyState = () => {
    if (query !== '') {
      return (
        <EmptyState>
          <img
            srcSet={`${emptySearch1x}, ${emptySearch2x} 2x`}
            width="177"
            height="160"
          />
          <p>{labels.noSearchResults(`${basePath}/add`)}</p>
        </EmptyState>
      )
    }

    if (selectedTab === 0) {
      return <Grid size="medium">{renderAddItem()}</Grid>
    }

    if (selectedTab === 1) {
      return (
        <EmptyState>
          <img
            srcSet={`${emptyArtists1x}, ${emptyArtists2x} 2x`}
            width="194"
            height="185"
          />
          <p>{labels.noFeatured}</p>
        </EmptyState>
      )
    }

    return null
  }

  return (
    <Tabs
      className="Tabs--modal"
      index={selectedTab}
      onChange={(index: number) => setSelectedTab(index)}
    >
      <Tab label={labels.publishedTab}>
        <Row columnsSize={[3]}>
          <div>
            <Input
              icon="search"
              placeholder={t('catalogs-management:search', 'Search')}
              value={query}
              onChange={updateSearchQuery}
            />
          </div>
        </Row>
        {albums && renderContent(albums)}
      </Tab>
      <Tab label={labels.featuredTab}>{albums && renderContent(albums)}</Tab>
    </Tabs>
  )
}

export default LibraryAlbumsManager
